// src/components/Dashboard/StageForm.js
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { TwitterPicker } from "react-color";
import { useAuth } from "../../common/contexts/AuthContext";
import { firestore } from "../../firebase";
import CloudflareR2Upload from "../../generic/services/CloudflareR2Upload";
import {
  CHAT_MODERATION_LEVELS,
  createDefaultChatSettings,
  normalizeChatSettings,
} from "../../chat/chatConfig";
import {
  calculateCountdownParts,
  formatScheduledStart,
  getBrowserTimezone,
  getTimezoneOptions,
  getUtcDateForZonedLocalDateTime,
  isValidTimezone,
  normalizeTimezone,
  toDateTimeLocalInputValue,
} from "../../utils/stageSchedule";

const createDefaultEventData = () => ({
  event_name: "",
  header_code: "",
  custom_slug: "",
  logo_url: "",
  title: "",
  colors: { header: "#111827", background: "#ffffff" },
  schedule: {
    enabled: false,
    timezone: getBrowserTimezone(),
    start_local: "",
    start_at: null,
  },
  password_protection: false,
  password: "",
  video_url: "",
  chat: createDefaultChatSettings(),
});

const normalizeEventData = (event) => {
  const defaults = createDefaultEventData();
  if (!event) return defaults;

  const scheduleTimezone = normalizeTimezone(
    event.schedule?.timezone || defaults.schedule.timezone
  );

  return {
    event_name: event.event_name || "",
    header_code: event.header_code || "",
    custom_slug: event.custom_slug || "",
    logo_url: event.logo_url || "",
    title: event.title || "",
    colors: {
      header: event.colors?.header || defaults.colors.header,
      background: event.colors?.background || defaults.colors.background,
    },
    schedule: {
      enabled: Boolean(event.schedule?.enabled),
      timezone: scheduleTimezone,
      start_local:
        event.schedule?.start_local ||
        toDateTimeLocalInputValue(event.schedule?.start_at, scheduleTimezone),
      start_at: event.schedule?.start_at || null,
    },
    password_protection: Boolean(event.password_protection),
    password: event.password || "",
    video_url: event.video_url || "",
    chat: normalizeChatSettings(event.chat),
  };
};

function ColorControl({ label, value, fallback, isOpen, setIsOpen, pickerRef, onChange }) {
  return (
    <div className="relative">
      <button
        type="button"
        title={`${label}: ${value || fallback}`}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-slate-300"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="block text-sm font-semibold text-slate-900">{label}</span>
        <span
          className="h-11 w-16 rounded-md border border-slate-200 shadow-inner"
          style={{ backgroundColor: value || fallback }}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          ref={pickerRef}
          className="absolute left-0 top-full z-20 mt-3 rounded-lg border border-slate-200 bg-white p-3 shadow-2xl"
        >
          <TwitterPicker
            color={value || fallback}
            onChangeComplete={(color) => onChange(color.hex)}
            onChange={(color) => onChange(color.hex)}
          />
          <button
            type="button"
            className="secondary-button mt-3 w-full"
            onClick={() => setIsOpen(false)}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

const buildCleanedSchedule = (schedule = {}) => {
  const timezone = schedule.timezone || getBrowserTimezone();
  const normalizedTimezone = normalizeTimezone(timezone);

  if (!schedule.enabled) {
    return {
      enabled: false,
      timezone: normalizedTimezone,
      start_local: "",
      start_at: null,
    };
  }

  const startLocal = (schedule.start_local || "").trim().slice(0, 16);
  if (!startLocal) {
    throw new Error("Set the scheduled start date and time, or turn countdown off.");
  }

  if (!isValidTimezone(timezone)) {
    throw new Error("Choose a supported timezone for this page.");
  }

  const startAt = getUtcDateForZonedLocalDateTime(startLocal, timezone);
  if (!startAt) {
    throw new Error("Choose a real start date and time for the selected timezone.");
  }

  return {
    enabled: true,
    timezone,
    start_local: startLocal,
    start_at: startAt,
  };
};

export default function StageForm({ initialEvent = null, onCreated, onSaved } = {}) {
  const { currentUser, accountData, chatEntitled } = useAuth();
  const isEditing = Boolean(initialEvent?.id);
  const [eventData, setEventData] = useState(() => normalizeEventData(initialEvent));
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [showHeaderPicker, setShowHeaderPicker] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const headerPickerRef = useRef(null);
  const backgroundPickerRef = useRef(null);
  const logoUrlRef = useRef(normalizeEventData(initialEvent).logo_url);
  const validSlugPattern = /^[a-zA-Z0-9-_]*$/;

  const resetForm = () => {
    const normalizedEvent = normalizeEventData(initialEvent);
    logoUrlRef.current = normalizedEvent.logo_url;
    setEventData(normalizedEvent);
    setError("");
    setShowHeaderPicker(false);
    setShowBackgroundPicker(false);
  };

  useEffect(() => {
    const normalizedEvent = normalizeEventData(initialEvent);
    logoUrlRef.current = normalizedEvent.logo_url;
    setEventData(normalizedEvent);
    setError("");
    setShowHeaderPicker(false);
    setShowBackgroundPicker(false);
  }, [initialEvent]);

  const handleSlugChange = (e) => {
    const value = e.target.value;

    if (validSlugPattern.test(value)) {
      setError("");
      handleInputChange(e);
    } else {
      setError("Use letters, numbers, hyphens, or underscores only.");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        headerPickerRef.current &&
        !headerPickerRef.current.contains(event.target)
      ) {
        setShowHeaderPicker(false);
      }
      if (
        backgroundPickerRef.current &&
        !backgroundPickerRef.current.contains(event.target)
      ) {
        setShowBackgroundPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    if (isLogoUploading) {
      setError("Wait for the logo upload to finish before saving this page.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    let didSave = false;

    try {
      const cleanedStage = {
        event_name: eventData.event_name.trim(),
        header_code: eventData.header_code || "",
        custom_slug: eventData.custom_slug.trim(),
        logo_url: (logoUrlRef.current || eventData.logo_url || "").trim(),
        title: eventData.title.trim() || eventData.event_name.trim(),
        colors: {
          header: eventData.colors.header || "#111827",
          background: eventData.colors.background || "#ffffff",
        },
        schedule: buildCleanedSchedule(eventData.schedule),
        password_protection: Boolean(eventData.password_protection),
        password: eventData.password_protection ? eventData.password : "",
        video_url: eventData.video_url.trim(),
        // Only persist chat as enabled if the account is actually entitled.
        // Strips client tampering and auto-disables on plan downgrade re-save.
        chat: normalizeChatSettings({
          ...eventData.chat,
          enabled: chatEntitled && Boolean(eventData.chat?.enabled),
          moderation_enabled:
            chatEntitled &&
            Boolean(eventData.chat?.enabled) &&
            Boolean(eventData.chat?.moderation_enabled),
        }),
      };
      if (!currentUser) {
        setError("You must be signed in to save a page.");
        return;
      }

      const slugQuery = query(
        collection(firestore, "events"),
        where("custom_slug", "==", cleanedStage.custom_slug),
        where("is_public", "==", true)
      );
      const slugSnapshot = await getDocs(slugQuery);
      const hasSlugConflict = slugSnapshot.docs.some(
        (slugDoc) => slugDoc.id !== initialEvent?.id
      );

      if (hasSlugConflict) {
        setError("This page URL is already in use. Choose another slug.");
        return;
      }

      if (isEditing) {
        await updateDoc(doc(firestore, "events", initialEvent.id), cleanedStage);
        console.info(
          "PAGE_UPDATE_SUCCEEDED event_id=%s logo_url=%s",
          initialEvent.id,
          cleanedStage.logo_url || "none"
        );
        didSave = true;
      } else {
        if (!accountData) {
          setError("Account data could not be loaded. Refresh and try again.");
          return;
        }

        await addDoc(collection(firestore, "events"), {
          ...cleanedStage,
          user_id: currentUser.uid,
          account_id: accountData.id,
          created_date: new Date(),
          is_public: false,
        });

        console.info(
          "PAGE_CREATE_SUCCEEDED custom_slug=%s logo_url=%s",
          cleanedStage.custom_slug,
          cleanedStage.logo_url || "none"
        );
        resetForm();
        didSave = true;
      }
    } catch (error) {
      console.error(isEditing ? "STAGE_UPDATE_FAILED" : "STAGE_CREATE_FAILED", error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }

    if (didSave) {
      if (isEditing) onSaved?.();
      else onCreated?.();
    }
  }

  function handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    setEventData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  const handleLogoUrlChange = (url) => {
    logoUrlRef.current = url;
    setEventData((prev) => ({ ...prev, logo_url: url }));
  };

  const updateColor = (key, value) => {
    setEventData((prev) => ({
      ...prev,
      colors: { ...prev.colors, [key]: value },
    }));
  };

  const updateSchedule = (key, value) => {
    setEventData((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [key]: value },
    }));
  };

  const updateChat = (key, value) => {
    setEventData((prev) => ({
      ...prev,
      chat: { ...prev.chat, [key]: value },
    }));
  };

  const timezoneOptions = getTimezoneOptions();
  const selectedTimezone = eventData.schedule.timezone || getBrowserTimezone();
  const visibleTimezoneOptions = timezoneOptions.includes(selectedTimezone)
    ? timezoneOptions
    : [selectedTimezone, ...timezoneOptions];
  const scheduleStartDate = eventData.schedule.enabled
    ? getUtcDateForZonedLocalDateTime(eventData.schedule.start_local, selectedTimezone)
    : null;
  const scheduleCountdown = scheduleStartDate ? calculateCountdownParts(scheduleStartDate) : null;
  const schedulePreviewText = !eventData.schedule.enabled
    ? "Countdown is off. The video shows as soon as the page is available."
    : !eventData.schedule.start_local
    ? "Pick a start date and time to turn the page into a countdown."
    : !scheduleStartDate
    ? "This start time is not valid for the selected timezone."
    : scheduleCountdown?.isComplete
    ? "The selected time has passed. Viewers will see the video immediately."
    : `Countdown will show ${scheduleCountdown.days}d ${scheduleCountdown.hours}h ${scheduleCountdown.minutes}m.`;

  return (
    <form onSubmit={handleSubmit} className="space-y-7">
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
          {error}
        </div>
      )}

      <div className="space-y-5">
        <div className="field-group">
          <label htmlFor="event_name" className="field-label">
            Page name
          </label>
          <input
            id="event_name"
            name="event_name"
            type="text"
            value={eventData.event_name}
            onChange={handleInputChange}
            required
            placeholder="Quarterly customer briefing"
            className="field-input"
          />

        </div>

        <div className="field-group">
          <label htmlFor="title" className="field-label">
            Header title
          </label>
          <input
            id="title"
            name="title"
            type="text"
            value={eventData.title}
            onChange={handleInputChange}
            placeholder="Optional"
            className="field-input"
            title="Leave blank to use the page name"
          />
        </div>

        <div className="field-group">
          <label htmlFor="custom_slug" className="field-label">
            Page URL
          </label>
          <div className="flex min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm focus-within:border-[#FF3A20] focus-within:ring-4 focus-within:ring-orange-100">
            <span className="hidden select-none items-center border-r border-slate-200 bg-slate-50 px-3 font-mono text-xs text-slate-500 sm:flex">
              app.vdopage.com/s/
            </span>
            <input
              id="custom_slug"
              name="custom_slug"
              type="text"
              value={eventData.custom_slug}
              onChange={handleSlugChange}
              required
              placeholder="customer-briefing"
              className="block min-w-0 flex-1 border-0 bg-transparent px-3.5 py-3 text-sm text-slate-950 outline-none placeholder:text-slate-400 focus:ring-0"
            />
          </div>

        </div>

        <div className="field-group">
          <label className="field-label">Logo</label>
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/70 p-4">
            {eventData.logo_url && (
              <div className="mb-4 flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-3">
                <img
                  src={eventData.logo_url}
                  alt="Current page logo"
                  className="h-16 w-24 rounded-md border border-slate-200 bg-slate-50 object-contain p-2"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900">Current logo</p>
                  <p className="mt-1 truncate font-mono text-xs text-slate-500">{eventData.logo_url}</p>
                </div>
                <button
                  type="button"
                  className="ghost-button text-rose-600 hover:text-rose-700"
                  onClick={() => handleLogoUrlChange("")}
                >
                  Remove
                </button>
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-900">Upload a logo file</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Upload PNG, JPG, or SVG to VdoPage storage. The generated public URL will be saved on the page.
                </p>
                <div className="mt-4">
                  <CloudflareR2Upload
                    setImageUrl={handleLogoUrlChange}
                    onUploadStateChange={setIsLogoUploading}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <label htmlFor="logo_url" className="text-sm font-semibold text-slate-900">
                  Or paste a logo URL
                </label>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Use a public image URL from your own CDN or website. Uploading a file will replace this value.
                </p>
                <input
                  id="logo_url"
                  name="logo_url"
                  type="url"
                  value={eventData.logo_url}
                  onChange={(event) => handleLogoUrlChange(event.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="field-input mt-4"
                />
                <p className="field-helper mt-2">Leave blank to show the header title instead of a logo.</p>
              </div>
            </div>
          </div>

        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ColorControl
            label="Header color"
            value={eventData.colors.header}
            fallback="#111827"
            isOpen={showHeaderPicker}
            setIsOpen={setShowHeaderPicker}
            pickerRef={headerPickerRef}
            onChange={(value) => updateColor("header", value)}
          />
          <ColorControl
            label="Page color"
            value={eventData.colors.background}
            fallback="#ffffff"
            isOpen={showBackgroundPicker}
            setIsOpen={setShowBackgroundPicker}
            pickerRef={backgroundPickerRef}
            onChange={(value) => updateColor("background", value)}
          />
        </div>

        <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex items-start gap-3">
            <input
              id="schedule_enabled"
              type="checkbox"
              checked={eventData.schedule.enabled}
              onChange={(event) => updateSchedule("enabled", event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF3A20] focus:ring-[#FF3A20]"
            />
            <div className="min-w-0 flex-1">
              <label htmlFor="schedule_enabled" className="block text-sm font-semibold text-slate-900">
                Show countdown before the stream starts
              </label>
              <p className="field-helper mt-1">
                Viewers see days, hours, and minutes until the scheduled start time. The start time can be edited after the stream has started.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="field-group">
              <label htmlFor="schedule_start_local" className="field-label">
                Stream start date and time
              </label>
              <input
                id="schedule_start_local"
                type="datetime-local"
                value={eventData.schedule.start_local}
                onChange={(event) => updateSchedule("start_local", event.target.value)}
                required={eventData.schedule.enabled}
                disabled={!eventData.schedule.enabled}
                className="field-input disabled:bg-slate-100 disabled:text-slate-400"
              />
              <p className="field-helper">Set the wall-clock time viewers should count down to.</p>
            </div>

            <div className="field-group">
              <label htmlFor="schedule_timezone" className="field-label">
                Page timezone
              </label>
              <select
                id="schedule_timezone"
                value={selectedTimezone}
                onChange={(event) => updateSchedule("timezone", event.target.value)}
                disabled={!eventData.schedule.enabled}
                className="field-input disabled:bg-slate-100 disabled:text-slate-400"
              >
                {visibleTimezoneOptions.map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </select>
              <p className="field-helper">Defaults to this computer's timezone, but can be changed.</p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Countdown preview
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{schedulePreviewText}</p>
                {scheduleStartDate && (
                  <p className="mt-1 text-xs text-slate-500">
                    Starts {formatScheduledStart(scheduleStartDate, selectedTimezone)}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-950 p-2 text-white">
                {[
                  ["DD", scheduleCountdown?.days || 0],
                  ["HH", scheduleCountdown?.hours || 0],
                  ["MM", scheduleCountdown?.minutes || 0],
                ].map(([label, value]) => (
                  <div key={label} className="min-w-[56px] rounded-md bg-white/10 px-2 py-2 text-center">
                    <p className="font-mono text-xl font-semibold leading-none">
                      {String(value).padStart(2, "0")}
                    </p>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/55">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <input
              id="password_protection"
              name="password_protection"
              type="checkbox"
              checked={eventData.password_protection}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF3A20] focus:ring-[#FF3A20]"
            />
            <div className="min-w-0 flex-1">
              <label
                htmlFor="password_protection"
                className="block text-sm font-semibold text-slate-900"
              >
                Enable password protection
              </label>
              {eventData.password_protection && (
                <input
                  type="password"
                  name="password"
                  value={eventData.password}
                  onChange={handleInputChange}
                  required
                  className="field-input mt-3"
                  placeholder="Set a password"
                />
              )}
            </div>
          </div>
        </div>

        <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex items-start gap-3">
            <input
              id="chat_enabled"
              type="checkbox"
              checked={chatEntitled && eventData.chat.enabled}
              disabled={!chatEntitled}
              onChange={(event) => updateChat("enabled", event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF3A20] focus:ring-[#FF3A20] disabled:opacity-40"
            />
            <div className="min-w-0 flex-1">
              <label htmlFor="chat_enabled" className="block text-sm font-semibold text-slate-900">
                Enable live viewer chat
              </label>
              <p className="field-helper mt-1">
                {chatEntitled
                  ? "Viewers can chat in a sidebar on the public page. No login required."
                  : "Live viewer chat is available on the Monthly + Chat and Annual + Chat plans. Upgrade in Billing to enable it."}
              </p>
            </div>
          </div>

          {chatEntitled && eventData.chat.enabled && (
            <div className="mt-4 space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <input
                    id="chat_moderation_enabled"
                    type="checkbox"
                    checked={eventData.chat.moderation_enabled}
                    onChange={(event) => updateChat("moderation_enabled", event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#FF3A20] focus:ring-[#FF3A20]"
                  />
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor="chat_moderation_enabled"
                      className="block text-sm font-semibold text-slate-900"
                    >
                      Enable AI moderation
                    </label>
                    <p className="field-helper mt-1">
                      Optional. When off, messages are still sanitized and length-capped, but not
                      content-checked.
                    </p>
                  </div>
                </div>

                {eventData.chat.moderation_enabled && (
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="field-group">
                      <label htmlFor="chat_moderation_level" className="field-label">
                        Moderation level
                      </label>
                      <select
                        id="chat_moderation_level"
                        value={eventData.chat.moderation_level}
                        onChange={(event) => updateChat("moderation_level", event.target.value)}
                        className="field-input"
                      >
                        {CHAT_MODERATION_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level === "low"
                              ? "Low — relaxed; swearing allowed, blocks hate/abuse"
                              : level === "medium"
                              ? "Medium — no swearing; mature topics allowed (clean language)"
                              : "High — professional; civil and on-topic only"}
                          </option>
                        ))}
                      </select>
                      <p className="field-helper">
                        Low suits gaming/community; medium keeps language clean but allows mature topics;
                        high suits government/corporate. All levels use your context hint.
                      </p>
                    </div>

                    <div className="field-group">
                      <label htmlFor="chat_context_hint" className="field-label">
                        Context hint (optional)
                      </label>
                      <input
                        id="chat_context_hint"
                        type="text"
                        value={eventData.chat.context_hint}
                        onChange={(event) => updateChat("context_hint", event.target.value)}
                        maxLength={280}
                        placeholder="e.g. oncology patient Q&A, FPS tournament, city council"
                        className="field-input"
                      />
                      <p className="field-helper">
                        Helps the moderator judge in context (e.g. allow clinical terms in a medical event).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="field-group">
          <label htmlFor="video_url" className="field-label">
            Video URL
          </label>
          <input
            id="video_url"
            name="video_url"
            type="url"
            value={eventData.video_url}
            onChange={handleInputChange}
            required
            placeholder="https://example.com/video.mp4, .m3u8, YouTube, Vimeo, or embed URL"
            className="field-input"
          />

        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
        <button type="button" className="secondary-button" onClick={resetForm}>
          Reset
        </button>
        <button
          type="submit"
          className="primary-button"
          disabled={isSubmitting || isLogoUploading}
        >
          {isLogoUploading
            ? "Uploading logo..."
            : isSubmitting
            ? isEditing
              ? "Saving..."
              : "Creating..."
            : isEditing
            ? "Save changes"
            : "Create page"}
        </button>
      </div>
    </form>
  );
}
